/**
 * CalculatorFab — small floating Windows-style calculator accessible from
 * every Doserne page. Keyboard shortcuts: digits, + − × ÷, Enter = equals,
 * Backspace = delete, Escape = clear, c = clear. The last 5 computations
 * are kept in a mini history list for quick re-use.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Calculator as CalcIcon, X, Delete, History, Minus } from "lucide-react";

const BTN = "h-11 rounded-xl text-base font-black transition active:scale-95 select-none";
const NUM = `${BTN} bg-slate-100 hover:bg-slate-200 text-slate-800`;
const OP  = `${BTN} bg-slate-900 hover:bg-slate-800 text-white`;
const FN  = `${BTN} bg-slate-200 hover:bg-slate-300 text-slate-700 text-sm`;
const EQ  = `${BTN} bg-cyan-600 hover:bg-cyan-500 text-white`;

const OP_LABEL = { "+": "+", "-": "−", "*": "×", "/": "÷" };

function fmt(n) {
  if (!isFinite(n)) return "Err";
  const s = n.toLocaleString("ro-RO", { maximumFractionDigits: 10 });
  return s;
}

function compute(a, op, b) {
  const x = Number(a); const y = Number(b);
  switch (op) {
    case "+": return x + y;
    case "-": return x - y;
    case "*": return x * y;
    case "/": return y === 0 ? NaN : x / y;
    default: return y;
  }
}

export default function CalculatorFab() {
  const [open, setOpen] = useState(false);
  // Position persisted in localStorage; default = middle-left of viewport.
  const [pos, setPos] = useState(() => {
    try {
      const raw = localStorage.getItem("calc_pos");
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return { x: 20, y: Math.max(120, Math.floor((window.innerHeight || 800) / 2) - 24) };
  });
  const dragRef = useRef({ startX: 0, startY: 0, originX: 0, originY: 0, moved: false, dragging: false });
  const posRef = useRef(pos);
  useEffect(() => { posRef.current = pos; }, [pos]);
  const savePos = (p) => {
    try { localStorage.setItem("calc_pos", JSON.stringify(p)); } catch { /* ignore */ }
  };
  const onFabPointerDown = (e) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, originX: posRef.current.x, originY: posRef.current.y, moved: false, dragging: true };
    const onMove = (ev) => {
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      if (!dragRef.current.moved && Math.abs(dx) + Math.abs(dy) < 4) return;
      dragRef.current.moved = true;
      const nx = Math.max(0, Math.min(window.innerWidth - 48, dragRef.current.originX + dx));
      const ny = Math.max(0, Math.min(window.innerHeight - 48, dragRef.current.originY + dy));
      setPos({ x: nx, y: ny });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      if (dragRef.current.moved) savePos(posRef.current);
      // Click when NOT dragged → toggle open.
      else setOpen((v) => !v);
      dragRef.current.dragging = false;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const [disp, setDisp] = useState("0");   // what's shown
  const [prev, setPrev] = useState(null);   // previous operand (string)
  const [op, setOp] = useState(null);
  const [justEval, setJustEval] = useState(false);
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("calc_history") || "[]"); } catch { return []; }
  });
  const panelRef = useRef(null);

  const pushHistory = (entry) => {
    const next = [entry, ...history].slice(0, 5);
    setHistory(next);
    try { localStorage.setItem("calc_history", JSON.stringify(next)); } catch { /* ignore */ }
  };

  const inputDigit = (d) => {
    setDisp((cur) => {
      if (justEval) { setJustEval(false); return d === "." ? "0." : d; }
      if (d === "." && cur.includes(".")) return cur;
      if (cur === "0" && d !== ".") return d;
      return cur + d;
    });
  };

  const toggleSign = () => setDisp((cur) => (cur.startsWith("-") ? cur.slice(1) : (cur === "0" ? cur : "-" + cur)));
  const pct = () => setDisp((cur) => fmt(Number(cur.replace(/[^0-9.-]/g, "")) / 100));

  const applyOp = (next) => {
    const curNum = Number(disp.replace(/[^0-9.-]/g, ""));
    if (prev !== null && op && !justEval) {
      const result = compute(prev, op, curNum);
      setPrev(String(result));
      setDisp(fmt(result));
    } else {
      setPrev(String(curNum));
    }
    setOp(next);
    setJustEval(true);
  };

  const equals = () => {
    if (op === null || prev === null) return;
    const curNum = Number(disp.replace(/[^0-9.-]/g, ""));
    const result = compute(prev, op, curNum);
    const expr = `${fmt(Number(prev))} ${OP_LABEL[op]} ${fmt(curNum)} = ${fmt(result)}`;
    pushHistory(expr);
    setDisp(fmt(result));
    setPrev(null); setOp(null); setJustEval(true);
  };

  const clear = () => { setDisp("0"); setPrev(null); setOp(null); setJustEval(false); };
  const back = () => setDisp((cur) => {
    if (justEval) return cur;
    if (cur.length <= 1 || (cur.length === 2 && cur.startsWith("-"))) return "0";
    return cur.slice(0, -1);
  });

  // Keyboard support when open.
  const handleKey = useCallback((e) => {
    if (!open) return;
    // Don't swallow keys when typing in another input/textarea.
    const t = e.target;
    if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
    if (/^[0-9]$/.test(e.key)) { e.preventDefault(); inputDigit(e.key); return; }
    if (e.key === ".") { e.preventDefault(); inputDigit("."); return; }
    if (["+", "-", "*", "/"].includes(e.key)) { e.preventDefault(); applyOp(e.key); return; }
    if (e.key === "Enter" || e.key === "=") { e.preventDefault(); equals(); return; }
    if (e.key === "Backspace") { e.preventDefault(); back(); return; }
    if (e.key === "Escape" || e.key.toLowerCase() === "c") { e.preventDefault(); clear(); return; }
    if (e.key === "%") { e.preventDefault(); pct(); return; }
  }, [open, disp, prev, op, justEval]); // eslint-disable-line

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  return (
    <>
      <button type="button" onPointerDown={onFabPointerDown} data-testid="calc-fab"
        title="Calculator — click deschide, drag pentru a muta"
        style={{ left: `${pos.x}px`, top: `${pos.y}px`, touchAction: "none" }}
        className={`fixed z-[55] w-12 h-12 rounded-full shadow-xl flex items-center justify-center transition-colors cursor-grab active:cursor-grabbing ${open ? "bg-slate-900 text-cyan-300" : "bg-cyan-600 hover:bg-cyan-500 text-white"}`}>
        {open ? <X className="w-5 h-5" /> : <CalcIcon className="w-5 h-5" />}
      </button>

      {open && (
        <div ref={panelRef} data-testid="calc-panel"
          style={{ left: `${Math.min(pos.x, (window.innerWidth || 1200) - 300)}px`, top: `${Math.max(10, pos.y - 10)}px`, transform: pos.y > (window.innerHeight || 800) - 460 ? "translateY(-100%)" : "translateY(60px)" }}
          className="fixed z-[55] w-[290px] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between px-3.5 py-2 bg-slate-900 text-white">
            <div className="text-[10px] font-black uppercase tracking-widest text-cyan-300 inline-flex items-center gap-1.5"><CalcIcon className="w-3.5 h-3.5" /> Calculator</div>
            <button onClick={() => setOpen(false)} className="w-6 h-6 rounded hover:bg-white/10 flex items-center justify-center"><Minus className="w-3.5 h-3.5" /></button>
          </div>
          {/* Display */}
          <div className="px-3.5 py-2.5 bg-slate-50 border-b border-slate-100 text-right">
            {op && prev !== null && (
              <div className="text-[11px] text-slate-400 h-4 font-bold">{fmt(Number(prev))} {OP_LABEL[op]}</div>
            )}
            <div className="text-3xl font-black text-slate-900 truncate" data-testid="calc-display">{disp}</div>
          </div>
          {/* Buttons */}
          <div className="grid grid-cols-4 gap-1.5 p-2.5">
            <button className={FN} onClick={clear} data-testid="calc-clear">C</button>
            <button className={FN} onClick={toggleSign}>±</button>
            <button className={FN} onClick={pct}>%</button>
            <button className={FN} onClick={back}><Delete className="w-4 h-4 inline" /></button>

            <button className={NUM} onClick={() => inputDigit("7")}>7</button>
            <button className={NUM} onClick={() => inputDigit("8")}>8</button>
            <button className={NUM} onClick={() => inputDigit("9")}>9</button>
            <button className={OP} onClick={() => applyOp("/")}>÷</button>

            <button className={NUM} onClick={() => inputDigit("4")}>4</button>
            <button className={NUM} onClick={() => inputDigit("5")}>5</button>
            <button className={NUM} onClick={() => inputDigit("6")}>6</button>
            <button className={OP} onClick={() => applyOp("*")}>×</button>

            <button className={NUM} onClick={() => inputDigit("1")}>1</button>
            <button className={NUM} onClick={() => inputDigit("2")}>2</button>
            <button className={NUM} onClick={() => inputDigit("3")}>3</button>
            <button className={OP} onClick={() => applyOp("-")}>−</button>

            <button className={`${NUM} col-span-2`} onClick={() => inputDigit("0")}>0</button>
            <button className={NUM} onClick={() => inputDigit(".")}>,</button>
            <button className={OP} onClick={() => applyOp("+")}>+</button>

            <button className={`${EQ} col-span-4`} onClick={equals} data-testid="calc-equals">=</button>
          </div>
          {/* History */}
          {history.length > 0 && (
            <div className="border-t border-slate-100 px-3 py-2 bg-slate-50">
              <div className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1"><History className="w-3 h-3" /> Istoric</div>
              <div className="space-y-0.5 text-[11px] text-slate-600 font-bold">
                {history.map((h, i) => (
                  <div key={i} className="truncate" title={h}>{h}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
